import { TrueType } from 'library/ts/utilities/modules/enums/TrueType';
import * as Type from 'library/ts/utilities/modules/Type';
import { TPropertyReplaceVO } from 'library/ts/utilities/modules/types/TPropertyReplaceVO';

/**
 * @description Sort Object alphabetically by property name
 * @see {@link https://stackoverflow.com/a/50163943} - idea from
 * @param object {Record<string, any>}
 * @return {Record<string, any>}
 */
export const sortByPropertyName = ( object: Record<string, any> ): Record<string, any> => {
	let output;
	if ( typeof object === TrueType.OBJECT && Object.keys( object ).length > 0 ) {
		output = {};
		Object.keys( object ).sort().forEach(
			( key ) => {
				output[ key ] = sortByPropertyName( object[ key ] );
			}
		);
		return output;
	}
	return object;
};

/**
 * @description Recursive object sorter by property name alphabetically asc
 * @see {@link http://whitfin.io/sorting-object-recursively-node-jsjavascript/}
 * @param object {Record<string, any>}
 * @return {Record<string, any>}
 */
export const sort = ( object: Record<string, any> ): Record<string, any> => {
	const sortedObj = {};
	const keys = Object.keys( object );

	keys.sort(
		( key1, key2 ) => {
			key1 = key1.toLowerCase();
			key2 = key2.toLowerCase();
			if ( key1 < key2 ) return -1;
			if ( key1 > key2 ) return 1;
			return 0;
		}
	);

	for ( const index in keys ) {
		const key = keys[ index ];
		if ( typeof object[ key ] == TrueType.OBJECT && !( object[ key ] instanceof Array ) ) {
			sortedObj[ key ] = sort( object[ key ] );
		} else {
			sortedObj[ key ] = object[ key ];
		}
	}

	return sortedObj;
};

/**
 * @description Two objects equality comparison
 * @param a {Record<string, any>|*}
 * @param b {Record<string, any>|*}
 * @return {Boolean}
 */
export const equal = ( a: Record<string, any> = {}, b: Record<string, any> = {} ): boolean => {
	// Create arrays of property names
	const aProps = Object.getOwnPropertyNames( a );
	const bProps = Object.getOwnPropertyNames( b );
	// If count of properties is different,
	// objects are not equivalent
	if ( aProps.length !== bProps.length ) {
		return false;
	}
	for ( let i = 0; i < aProps.length; i++ ) {
		const propName = aProps[ i ];
		// If values of same property are not equal,
		// objects are not equivalent
		if ( a[ propName ] !== b[ propName ] ) {
			return false;
		}
	}
	// If we made it this far, objects
	// are considered equivalent
	return true;
};

/**
 * @description Object equality comparison via JSON
 * @param a {Record<string, any>|*}
 * @param b {Record<string, any>|*}
 * @return {Boolean}
 */
export const jsonEqual = ( a: Record<string, any> | any, b: Record<string, any> | any ): boolean => {
	return JSON.stringify( a ) === JSON.stringify( b );
};

/**
 * @description Clone simple Object
 * @param object (Object)
 * @return {Object}
 */
export const clone = ( object: any ): any => {
	if ( Type.isObject( object ) === false || Type.isNull( object ) ) return object;
	const copy = {};
	Object.keys( object ).forEach(
		( key ) => {
			copy[ key ] = object[ key ];
		}
	);
	return copy;
};

/**
 * @description Deep clone nested object with JSON object
 * @param object
 * @returns {any}
 */
export const cloneDeep = ( object: any ): any => {
	return JSON.parse( JSON.stringify( object ) );
};

/**
 * @description Clones + cleans up object for postMessage use
 * @param object
 * @returns {any}
 */
export const cloneForPostMessage = ( object: any ): any => {
	return removeEmptyKeys( cloneDeep( object ) );
};

/**
 * @description Deep merge two objects. Suffers from merging array info enumerated object (TODO: take array into account + update / extend this method?)
 * @param {Record<string, any>} target
 * @param {...Record<string, any>} sources
 * @return {Record<string, any>}
 */
export const mergeDeep = ( target: Record<string, any>, ...sources ): Record<string, any> => {
	if ( !sources.length ) return target;
	const source = sources.shift();

	if ( Type.isObject( target ) && Type.isObject( source ) ) {
		for ( const key in source ) {
			if ( Object.prototype.hasOwnProperty.call( source, key ) ) {
				if ( Type.isObject( source[ key ] ) ) {
					if ( !target[ key ] ) Object.assign( target, { [ key ]: {} } );
					mergeDeep( target[ key ], source[ key ] );
				} else {
					Object.assign( target, { [ key ]: source[ key ] } );
				}
			}
		}
	}

	return mergeDeep( target, ...sources );
};

/**
 * @description Side-fill for ES6 `Object.assign` function. See https://tc39.es/ecma262/#sec-object.assign
 * @param {{[p: string]: any}} target
 * @param {{[p: string]: any}} args
 * @returns {{[p: string]: any}}
 */
export const assign = ( target: { [ key: string ]: any }, ...args: { [ key: string ]: any }[] ): { [ key: string ]: any } => {
	return Object.assign( target || {}, ...args );
};

/** *
 * @description recursively freezes an object's properties and property values
 * @param {Record<string, any>} target
 */
export const deepFreeze = ( target: Record<string, any> ): Record<string, any> => {
	const keys: ( string )[] = Object.keys( target );
	keys.forEach( ( key: string ) => {
		if ( typeof target[ key ] === 'object' && Object.isFrozen( target[ key ] ) === false ) {
			deepFreeze( target[ key ] );
		}
	} );

	return Object.freeze( target );
};

/**
 * @description Removed empty properties from deep nested object
 * @param {Record<string, any>} obj
 * @returns {any}
 */
export const removeEmptyKeys = ( obj: Record<string, any> ): any => {
	if ( Type.isObject( obj ) ) {
		Object.keys( obj ).forEach(
			( key ) => {
				const value = obj[ key ];
				// if value is empty
				if ( Type.isEmpty( value ) ) {
					delete obj[ key ]; // delete property from the object
				}
				removeEmptyKeys( value ); // recurse
			}
		);
	}
	return obj;
};

/**
 * @description Utility method for adding / setting properties from source object on target object using fill out map
 * @param {Record<string, any>} sourceObject
 * @param {Record<string, any>} targetObject
 * @param {TPropertyReplaceVO[]} fillOutMap
 * @returns {Record<string, any>}
 */
export const mapMerge = ( sourceObject: Record<string, any> = {}, targetObject: Record<string, any> = {}, fillOutMap: TPropertyReplaceVO[] = [] ): Record<string, any> => {
	if ( !Type.isObject( sourceObject ) ) {
		sourceObject = {};
	}

	if ( !Type.isObject( targetObject ) ) {
		targetObject = {};
	}

	if ( !Type.isArray( fillOutMap ) ) {
		fillOutMap = [];
	}

	fillOutMap.forEach(
		( obj ) => {
			for ( let i = 0; i < obj.from.length; i++ ) {
				const oneFrom = obj.from[ i ];
				if ( !Type.isEmpty( sourceObject[ oneFrom ] ) ) {
					targetObject[ obj.to ] = sourceObject[ oneFrom ];
				}
				// check if we have default value for this property
				if ( !Type.isEmpty( obj?.default ) && Type.isEmpty( targetObject[ obj.to ] ) ) {
					targetObject[ obj.to ] = obj.default;
				}
				// check if we have explicit override value for this property
				if ( !Type.isEmpty( obj?.override ) ) {
					targetObject[ obj.to ] = obj.override;
				}
			}
		}
	);

	return targetObject;
};

/**
 * @description Reads a value at a dot-notation path from an object.
 * Returns `null` if any segment along the path does not exist.
 *
 * Duplicated from `DOM.getDeepVal` to give this utility its correct semantic home.
 * `DOM.getDeepVal` remains in place for existing call sites — a future cleanup ticket
 * will consolidate both and migrate all references to `utils.Object.getDeepVal`.
 *
 * @param {Record<string, any>} object - Source object
 * @param {string} path - Dot-notation path (e.g. `'playerOptions.network.timeout'`)
 * @returns {any} Value at the path, or `null` if any segment is missing
 */
export const getDeepVal = ( object: Record<string, any>, path: string ): any => {
	if ( Type.isUndefined( object ) || Type.isNull( object ) ) return null;
	const segments = path.split( /[\.\[\]\"\']{1,2}/ );
	for ( let i = 0; i < segments.length; i++ ) {
		if ( segments[ i ] === '' ) continue;
		object = object[ segments[ i ] ];
		if ( Type.isUndefined( object ) || Type.isNull( object ) ) return null;
	}
	return object;
};

/**
 * @description Writes a value at a dot-notation path in an object, creating
 * intermediate objects along the path as needed. Companion to `getDeepVal`.
 *
 * @param {Record<string, any>} object - Target object (mutated in place)
 * @param {string} path - Dot-notation path (e.g. `'playerOptions.network.timeout'`)
 * @param {any} value - Value to write at the path
 */
export const setDeepVal = ( object: Record<string, any>, path: string, value: any ): void => {
	const keys = path.split( '.' );
	const lastKey = keys.pop();
	// Walk to the parent, creating intermediate objects as needed
	const target = keys.reduce( ( current, key ) => {
		if ( Type.isUndefined( current[ key ] ) || Type.isNull( current[ key ] ) ) {
			current[ key ] = {};
		}
		return current[ key ];
	}, object );
	target[ lastKey ] = value;
};

/**
 * @description Returns TypeScript enum converted to object with properties using ES5 syntax
 * @param {Record<string, unknown>} enumType
 * @returns {{[p: string]: string}}
 */
export const enumToObject = ( enumType: Record<string, unknown> ): { [ key: string ]: string } => {
	const values: { [ key: string ]: string } = {};

	for ( const key in enumType ) {
		if ( typeof enumType[ key ] === 'string' ) {
			values[ key ] = enumType[ key ];
		}
	}

	return values;
};